import { useEffect, useRef, useState } from "react";
import type {
  OwlBlock, FundCardsBlock, FundCompareBlock, ScoreRingBlock, ReturnsSparkBlock,
  SectorBarBlock, McapDonutBlock, BandGaugeBlock, UnderwaterBlock, StatTilesBlock,
  CategoryContextBlock, FlowBarsBlock, ValuationGaugeBlock,
  ConceptSketchBlock, DailyBriefBlock, HoldingsOverlapBlock, PlanCompareBlock,
  FundRatingChip,
} from "../lib/types";
import { FundChip } from "../components/FundChip";

/**
 * Generative-UI block renderer for the Owl chat (2026-07-06).
 *
 * The server (brain/owl_chat_blocks.py) emits a typed `blocks[]` array for fund
 * intents; this maps each block.type to an animated component so a "top 5 funds"
 * or "how good is X" answer arrives as moving visuals + a one-line caption
 * instead of a paragraph. Every number is server-verified — the client only
 * draws it. Inspired by vercel-labs/json-render, adapted server-owned for a RIA.
 */

const REDUCED = typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* Flip true just after mount, so CSS transitions animate from 0 -> target. Uses
 * setTimeout (not rAF) — rAF is throttled in headless/background tabs, which
 * would leave bars/rings stuck at 0; a short timeout fires reliably and still
 * lets the browser paint the initial 0 state first. */
function useMounted(): boolean {
  const [on, setOn] = useState(REDUCED);
  useEffect(() => {
    if (REDUCED) return;
    const id = setTimeout(() => setOn(true), 40);
    return () => clearTimeout(id);
  }, []);
  return on;
}

const TIER_HEX: Record<string, string> = {
  Healthy: "#059669", Stable: "#0284c7", Watch: "#d97706", Critical: "#dc2626", NR: "#64748b",
};
const tierHex = (t?: string | null) => TIER_HEX[t || "NR"] || "#64748b";
const signed = (n: number | null | undefined) => (n == null ? "–" : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`);

/* ────────────────────────── the renderer ────────────────────────── */

export function BlockRenderer({ blocks }: { blocks: OwlBlock[] }) {
  return (
    <div className="space-y-2.5">
      {blocks.map((b, i) => (
        <div key={i} className="blk-rise" style={{ animationDelay: REDUCED ? "0ms" : `${i * 70}ms` }}>
          <OneBlock block={b} />
        </div>
      ))}
    </div>
  );
}

function OneBlock({ block }: { block: OwlBlock }) {
  switch (block.type) {
    case "caption":       return <Caption text={block.text} />;
    case "fund_cards":    return <FundCards b={block} />;
    case "fund_compare":  return <FundCompare b={block} />;
    case "score_ring":    return <ScoreRing b={block} />;
    case "returns_spark": return <ReturnsSpark b={block} />;
    case "sector_bar":    return <SectorBar b={block} />;
    case "mcap_donut":    return <McapDonut b={block} />;
    case "band_gauge":    return <BandGauge b={block} />;
    case "underwater":    return <Underwater b={block} />;
    case "stat_tiles":    return <StatTiles b={block} />;
    case "category_context": return <CategoryContext b={block} />;
    case "flow_bars":     return <FlowBars b={block} />;
    case "valuation_gauge": return <ValuationGauge b={block} />;
    case "concept_sketch":  return <ConceptSketch b={block} />;
    case "daily_brief":     return <DailyBriefBlockView b={block} />;
    case "holdings_overlap": return <HoldingsOverlap b={block} />;
    case "plan_compare":  return <PlanCompare b={block} />;
    default:              return null;
  }
}

/* ────────────────────────── valuation gauge ────────────────────────── */
// Phase 4: the market's P/E vs its own history. A cheap → expensive track with a
// marker at the percentile; colour is paired with the plain-words band.
function ValuationGauge({ b }: { b: ValuationGaugeBlock }) {
  const pct = Math.max(0, Math.min(100, b.percentile ?? 0));
  const band =
    pct >= 60 ? "text-red-700" : pct >= 40 ? "text-amber-600" : "text-emerald-700";
  const marker =
    pct >= 60 ? "bg-red-500" : pct >= 40 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] text-slate-600">{b.label}</span>
        <span className="font-mono text-sm font-semibold text-nyvo-900">{b.value}</span>
      </div>
      <div
        className="relative mt-2 h-2 rounded-full"
        style={{ background: "linear-gradient(to right,#10b981,#f59e0b,#ef4444)" }}
      >
        <div
          className={`absolute top-1/2 h-3.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-sm ${marker} ring-2 ring-white`}
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[9px] uppercase tracking-wider text-slate-400">
        <span>Cheap</span>
        <span>Expensive</span>
      </div>
      {b.read && (
        <div className={`mt-1.5 text-[11px] ${band}`}>
          {b.percentile_label ?? `${pct}th`} percentile — {b.read}
        </div>
      )}
      {(b.as_of || b.source) && (
        <div className="mt-1 text-[9px] uppercase tracking-wider text-slate-400">
          {[b.as_of, b.source].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── flow bars (FII / DII) ────────────────────────── */
// Phase 4: FII vs DII net flows. Diverging bars from a center line — green for
// net inflow, red for net outflow. Colour is always paired with a signed number.
function FlowBars({ b }: { b: FlowBarsBlock }) {
  const unit = b.unit ? ` ${b.unit}` : "";
  const max = Math.max(1, ...b.bars.map((x) => Math.abs(x.value)));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      {(b.period || b.source) && (
        <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-400">
          {[b.period, b.source].filter(Boolean).join(" · ")}
        </div>
      )}
      <div className="space-y-2.5">
        {b.bars.map((bar) => {
          const pos = bar.value >= 0;
          const w = Math.round((Math.abs(bar.value) / max) * 50); // half-width max (diverging)
          return (
            <div key={bar.label}>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">{bar.label}</span>
                <span className={`font-mono font-semibold ${pos ? "text-emerald-700" : "text-red-700"}`}>
                  {pos ? "+" : "−"}₹{Math.abs(bar.value).toLocaleString("en-IN")}{unit}
                  <span className="ml-1 font-sans font-normal text-slate-400">
                    {pos ? "in" : "out"}
                  </span>
                </span>
              </div>
              {/* center-anchored bar: left half = outflow, right half = inflow */}
              <div className="mt-1 flex h-2 items-center">
                <div className="flex w-1/2 justify-end">
                  {!pos && <div className="h-2 rounded-l bg-red-500" style={{ width: `${w * 2}%` }} />}
                </div>
                <div className="h-3 w-px bg-slate-300" />
                <div className="flex w-1/2 justify-start">
                  {pos && <div className="h-2 rounded-r bg-emerald-500" style={{ width: `${w * 2}%` }} />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────────────── category context (2 categories) ────────────────────── */
// Recipe E: before comparing funds from DIFFERENT categories, show the two
// categories side by side so the reader picks the role first. Numbers are the
// typical Healthy fund's — labelled as such, never a promise.
function CategoryContext({ b }: { b: CategoryContextBlock }) {
  const fmtPct = (n?: number | null) =>
    typeof n === "number" && !Number.isNaN(n) ? `${n > 0 ? "+" : ""}${n.toFixed(1)}%` : "–";
  return (
    <div className="grid grid-cols-2 gap-2">
      {b.categories.slice(0, 2).map((c) => (
        <div key={c.name} className="rounded-lg border border-slate-200 bg-white p-2.5">
          <div className="text-xs font-semibold text-nyvo-900">{c.name}</div>
          {c.role && (
            <div className="mt-1 text-[10px] leading-snug text-slate-500">{c.role}</div>
          )}
          {(typeof c.healthy_median_3y_pct === "number"
            || (typeof c.n_healthy === "number" && typeof c.n_total === "number")) && (
          <div className="mt-2 space-y-0.5 text-[10px]">
            {typeof c.healthy_median_3y_pct === "number" && (
              <div className="flex justify-between">
                <span className="text-slate-400">Typical Healthy 3Y</span>
                <span className="font-mono font-semibold text-nyvo-900">
                  {fmtPct(c.healthy_median_3y_pct)}
                </span>
              </div>
            )}
            {typeof c.n_healthy === "number" && typeof c.n_total === "number" && (
              <div className="flex justify-between">
                <span className="text-slate-400">Healthy funds</span>
                <span className="font-mono text-slate-600">{c.n_healthy}/{c.n_total}</span>
              </div>
            )}
          </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────── caption ────────────────────────── */
function Caption({ text }: { text: string }) {
  return <p className="px-0.5 text-[13px] font-semibold text-nyvo-900">{text}</p>;
}

/* ────────────────────────── fund cards (top-N) ────────────────────────── */
function FundCards({ b }: { b: FundCardsBlock }) {
  return (
    <div className="space-y-1.5">
      {b.funds.map((f) => <FundChip key={f.scheme_code} chip={f} />)}
    </div>
  );
}

/* ────────────────────────── compare (2 funds) ────────────────────────── */
function FundCompare({ b }: { b: FundCompareBlock }) {
  const on = useMounted();
  const [a, c] = b.funds;
  const rows: { k: string; a: number | null; c: number | null; hib: boolean }[] = [
    { k: "1Y return", a: a.trail_1y_pct ?? null, c: c.trail_1y_pct ?? null, hib: true },
    { k: "3Y CAGR", a: a.trail_3y_cagr_pct ?? null, c: c.trail_3y_cagr_pct ?? null, hib: true },
    { k: "Sharpe", a: a.sharpe_3y ?? null, c: c.sharpe_3y ?? null, hib: true },
    { k: "Volatility", a: a.std_dev_3y_pct ?? null, c: c.std_dev_3y_pct ?? null, hib: false },
  ].filter((r) => r.a != null || r.c != null);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-2 grid grid-cols-2 gap-2">
        {b.funds.map((f) => (
          <div key={f.scheme_code} className="min-w-0">
            <TierPill tier={f.tier} />
            <p className="mt-1 truncate text-[11px] font-semibold text-nyvo-900" title={f.fund_name}>
              {f.fund_name}
            </p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const max = Math.max(Math.abs(r.a ?? 0), Math.abs(r.c ?? 0), 0.01);
          const aWin = r.a != null && r.c != null && (r.hib ? r.a >= r.c : r.a <= r.c);
          return (
            <div key={r.k}>
              <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">{r.k}</p>
              <div className="grid grid-cols-2 gap-2">
                <MiniBar value={r.a} max={max} on={on} align="right" win={aWin} suffix={r.k.includes("Sharpe") ? "" : "%"} />
                <MiniBar value={r.c} max={max} on={on} align="left" win={r.a != null && r.c != null && !aWin} suffix={r.k.includes("Sharpe") ? "" : "%"} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniBar({ value, max, on, align, win, suffix }: {
  value: number | null; max: number; on: boolean; align: "left" | "right"; win: boolean; suffix: string;
}) {
  const w = value == null ? 0 : Math.min(100, (Math.abs(value) / max) * 100);
  const dp = suffix === "%" ? 1 : 2;
  return (
    <div className={`flex items-center gap-1.5 ${align === "right" ? "flex-row-reverse" : ""}`}>
      <div className="h-4 flex-1 overflow-hidden rounded-sm bg-slate-100">
        <div className="blk-bar h-full rounded-sm" style={{
          width: on ? `${w}%` : "0%",
          background: win ? "#059669" : "#94a3b8",
          marginLeft: align === "right" ? "auto" : undefined,
        }} />
      </div>
      <span className={`w-11 shrink-0 text-[11px] tabular-nums ${win ? "font-bold text-emerald-700" : "text-slate-600"} ${align === "right" ? "text-left" : "text-right"}`}>
        {value == null ? "–" : `${value.toFixed(dp)}${suffix}`}
      </span>
    </div>
  );
}

/* ────────────────────────── score ring (single fund hero) ────────────────────────── */
/* The rating hero. The TIER is the verdict, never a score.
 *
 * Until 2026-09-10 this drew a ring filled to score/100 with "71 / 100" in the
 * middle. FundChip.tsx settled the rule back in May - "the composite score no
 * longer appears as a number anywhere" - and every other fund surface honoured
 * it; this hero did not. A composite is not a number a customer can act on, and
 * printing it invites an argument about 71 versus 75 that the number was never
 * built to settle.
 *
 * What replaces it is the badge the rest of the product already awards — the
 * founder's medal artwork, one per tier, the tier name in the ribbon — beside
 * the scale it was placed on. The badge says which; the four rungs say where
 * that sits, which a badge alone cannot tell someone seeing their first one.
 * Unrated is its own state, not the bottom rung.
 *
 * The ladder and the copy come from the API (tier_scale / tier_index /
 * tier_note / tier_badge) so this file and the app cannot drift from each other.
 * The fallbacks below are for an older payload, not a second source of truth.
 *
 * The artwork is a bundled asset, not a payload field — blocks[] is JSON.
 * owl/public/tier-badges/<key>.png, cut from the masters by
 * scripts/build_tier_badges.py. If it fails to load we fall back to the
 * coloured word, because a missing PNG must not cost a customer the verdict.
 */
const LADDER_FALLBACK = ["Healthy", "Stable", "Watch", "Critical"];
const badgeSrc = (key?: string | null, tier?: string) => {
  const k = (key || tier || "").toLowerCase();
  return LADDER_FALLBACK.some((t) => t.toLowerCase() === k) ? `/tier-badges/${k}.png` : null;
};

function ScoreRing({ b }: { b: ScoreRingBlock }) {
  const on = useMounted();
  const [artOk, setArtOk] = useState(true);
  const hex = tierHex(b.tier);
  const scale = b.tier_scale?.length ? b.tier_scale : LADDER_FALLBACK;
  const here = b.tier_index ?? scale.indexOf(b.tier);
  const rated = here >= 0;
  const art = rated && artOk ? badgeSrc(b.tier_badge, b.tier) : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-3">
        {art ? (
          <img
            src={art}
            alt={`${b.tier} rating`}
            width={64}
            height={64}
            className="h-16 w-16 shrink-0"
            style={{ opacity: on ? 1 : 0, transition: "opacity .35s ease" }}
            onError={() => setArtOk(false)}
          />
        ) : rated ? (
          /* artwork missing — the word still has to carry the verdict */
          <span
            className="inline-block shrink-0 rounded-full px-2.5 py-1 text-[12px] font-extrabold uppercase tracking-wider text-white"
            style={{ background: hex }}
          >
            {b.tier}
          </span>
        ) : (
          /* unrated: same footprint as a badge so the card keeps its rhythm,
           * and quiet, because this is the absence of a verdict and not one */
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-300 bg-slate-50 text-center text-[10px] font-semibold leading-tight text-slate-400">
            Not<br />rated
          </span>
        )}

        <div className="min-w-0 flex-1">
          {b.category && (
            <div className="truncate text-[11px] text-slate-500">{b.category}</div>
          )}
          {/* the four rungs, best first */}
          <div className="mt-1.5 flex gap-1" role="img"
               aria-label={rated ? `${b.tier}, rung ${here + 1} of ${scale.length}` : "Not rated"}>
            {scale.map((t, i) => {
              const isHere = rated && i === here;
              return (
                <div key={t} className="min-w-0 flex-1">
                  <div
                    className="h-1.5 rounded-full blk-draw"
                    style={{
                      background: isHere ? hex : "#eef2f7",
                      transform: on ? "scaleX(1)" : "scaleX(0)",
                      transformOrigin: "left",
                      transitionDelay: `${i * 60}ms`,
                    }}
                  />
                  <div
                    className={`mt-1 truncate text-[10px] ${
                      isHere ? "font-bold" : "font-medium text-slate-400"
                    }`}
                    style={isHere ? { color: hex } : undefined}
                    title={t}
                  >
                    {t}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {(b.tier_note || b.why) && (
        <p className="mt-2 text-[11px] leading-snug text-slate-600">
          {b.tier_note || b.why}
        </p>
      )}
    </div>
  );
}

/* ────────────────────────── returns sparkline ────────────────────────── */
function ReturnsSpark({ b }: { b: ReturnsSparkBlock }) {
  const on = useMounted();
  const vals = b.points.map((p) => p.v);
  const max = Math.max(...vals.map((v) => Math.abs(v ?? 0)), 1);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">{b.label}</p>
      <div className="flex items-end justify-between gap-2" style={{ height: 68 }}>
        {b.points.map((p, i) => {
          const v = p.v; const up = (v ?? 0) >= 0;
          const h = v == null ? 0 : (Math.abs(v) / max) * 46;
          return (
            <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1" style={{ height: "100%" }}>
              <span className={`text-[10px] tabular-nums ${up ? "text-emerald-700" : "text-red-600"}`}>{signed(v)}</span>
              <div className="flex w-full items-end justify-center" style={{ height: 46 }}>
                <div className="blk-bar w-4 rounded-t" style={{
                  height: on ? `${Math.max(h, v == null ? 0 : 2)}px` : "0px",
                  background: v == null ? "#e2e8f0" : up ? "#10b981" : "#ef4444",
                  transitionDelay: `${i * 60}ms`,
                }} />
              </div>
              <span className="text-[10px] font-semibold text-slate-500">{p.k}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────────────────── sector bar ────────────────────────── */
function SectorBar({ b }: { b: SectorBarBlock }) {
  const on = useMounted();
  const rows = b.rows.filter((r) => r.label && r.pct != null);
  const max = Math.max(...rows.map((r) => r.pct), 1);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">{b.title}</p>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={r.label} className="flex items-center gap-2">
            <span className="w-24 shrink-0 truncate text-[11px] text-nyvo-900" title={r.label}>{r.label}</span>
            <div className="h-3.5 flex-1 overflow-hidden rounded-sm bg-slate-100">
              <div className="blk-bar h-full rounded-sm bg-sky-400" style={{
                width: on ? `${(r.pct / max) * 100}%` : "0%", transitionDelay: `${i * 60}ms`,
              }} />
            </div>
            <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-slate-600">{r.pct.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────── market-cap donut ────────────────────────── */
function McapDonut({ b }: { b: McapDonutBlock }) {
  const on = useMounted();
  const segs = [
    { k: "Large", v: b.large ?? 0, c: "#0284c7" },
    { k: "Mid", v: b.mid ?? 0, c: "#38bdf8" },
    { k: "Small", v: b.small ?? 0, c: "#f59e0b" },
    { k: "Other", v: b.other ?? 0, c: "#cbd5e1" },
  ].filter((s) => s.v > 0.05);
  const total = segs.reduce((s, x) => s + x.v, 0) || 100;
  const R = 30, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3">
      <svg width="76" height="76" viewBox="0 0 76 76" className="shrink-0">
        <circle cx="38" cy="38" r={R} fill="none" stroke="#eef2f7" strokeWidth="10" />
        {segs.map((s, i) => {
          const frac = s.v / total;
          const off = C * (1 - frac);
          const rot = (acc / total) * 360 - 90;
          acc += s.v;
          return (
            <circle key={i} cx="38" cy="38" r={R} fill="none" stroke={s.c} strokeWidth="10"
              strokeDasharray={C} className="blk-draw"
              strokeDashoffset={on ? off : C}
              transform={`rotate(${rot} 38 38)`} style={{ transitionDelay: `${i * 120}ms` }} />
          );
        })}
      </svg>
      <div className="space-y-0.5">
        {segs.map((s) => (
          <div key={s.k} className="flex items-center gap-1.5 text-[11px]">
            <span className="inline-block h-2.5 w-2.5 rounded-xs" style={{ background: s.c }} />
            <span className="text-slate-600">{s.k}</span>
            <span className="font-semibold tabular-nums text-nyvo-900">{s.v.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────── downside band gauge ────────────────────────── */
const BAND_LABEL: Record<string, { text: string; c: string }> = {
  safer_than_category: { text: "Calmer than its category", c: "#059669" },
  typical_for_category: { text: "Typical for its category", c: "#0284c7" },
  riskier_than_category: { text: "Bumpier than its category", c: "#d97706" },
};
function BandGauge({ b }: { b: BandGaugeBlock }) {
  const on = useMounted();
  const meta = BAND_LABEL[b.position] || { text: b.position, c: "#64748b" };
  const lo = b.p25 ?? 0, hi = b.p75 ?? 0, fund = b.fund;
  const span = Math.max(hi - lo, 0.01);
  const domainLo = lo - span, domainHi = hi + span;
  const posOf = (v: number) => Math.max(0, Math.min(100, ((v - domainLo) / (domainHi - domainLo)) * 100));
  const bandL = posOf(lo), bandR = posOf(hi), mark = fund == null ? null : posOf(fund);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">Downside vs its category</p>
      <div className="relative mb-1.5 h-2.5 rounded-full bg-slate-100">
        <div className="absolute inset-y-0 rounded-full bg-slate-300"
          style={{ left: `${bandL}%`, width: `${Math.max(2, bandR - bandL)}%` }} />
        {mark != null && (
          <div className="blk-bar absolute -top-1 h-4.5 w-1.5 rounded-full"
            style={{ height: 18, top: -4, left: on ? `${mark}%` : "0%", background: meta.c,
                     transform: "translateX(-50%)", boxShadow: "0 0 0 3px #fff" }} />
        )}
      </div>
      <p className="text-[12px] font-semibold" style={{ color: meta.c }}>{meta.text}</p>
      <p className="mt-0.5 text-[10px] text-slate-400">Grey band = where most peers sit · lower = steadier</p>
    </div>
  );
}

/* ────────────────────────── underwater (drawdown) chart ────────────────────────── */
function Underwater({ b }: { b: UnderwaterBlock }) {
  const on = useMounted();
  const pts = b.points.filter((p) => typeof p.pct === "number");
  if (pts.length < 2) return null;
  const W = 260, H = 60;
  const min = Math.min(...pts.map((p) => p.pct), -0.01);
  const xOf = (i: number) => (i / (pts.length - 1)) * W;
  const yOf = (v: number) => (v / min) * H;      // 0 at top, min at bottom
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${xOf(i).toFixed(1)},${yOf(p.pct).toFixed(1)}`).join(" ");
  const area = `${line} L${W},0 L0,0 Z`;
  const pathRef = useRef<SVGPathElement>(null);
  const [len, setLen] = useState(0);
  useEffect(() => { if (pathRef.current) setLen(pathRef.current.getTotalLength()); }, []);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Worst dips (last ~3y)</p>
        {b.max_drawdown != null && (
          <span className="text-[11px] font-bold text-red-600">deepest {b.max_drawdown.toFixed(1)}%</span>
        )}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height: 60 }}>
        <path d={area} fill="#fee2e2" className={`blk-fade ${on ? "on" : ""}`} />
        <path ref={pathRef} d={line} fill="none" stroke="#ef4444" strokeWidth="1.5"
          strokeDasharray={len || undefined} className="blk-draw"
          strokeDashoffset={on ? 0 : (len || 0)} />
      </svg>
    </div>
  );
}

/* ────────────────────────── stat tiles ────────────────────────── */
function StatTiles({ b }: { b: StatTilesBlock }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      {b.title && <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">{b.title}</p>}
      <div className="grid grid-cols-3 gap-2">
        {b.tiles.map((t, i) => (
          <div key={i} className="rounded-lg bg-slate-50 px-2 py-1.5">
            <p className="text-[10px] text-slate-500">{t.label}</p>
            <p className="text-[13px] font-bold tabular-nums text-nyvo-900">
              {t.dir === "up" && "▲ "}{t.dir === "down" && "▼ "}{t.value}
            </p>
            {t.hint && <p className="text-[9px] text-slate-400">{t.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────── shared bits ────────────────────────── */
function TierPill({ tier }: { tier: FundRatingChip["tier"] }) {
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
      style={{ background: tierHex(String(tier)) }}>
      {tier}
    </span>
  );
}

/* ────────────────────────── concept sketch ────────────────────────── */
// 2026-07-24. The server names WHICH explainer and writes the copy; the drawing
// lives here, so nothing on screen can go stale or read as a claim about a fund.
// Each sketch is two labelled halves — the contrast IS the explanation.

function SketchPair({ left, right, leftNote, rightNote }: {
  left: string; right: string; leftNote: string; rightNote: string;
}) {
  const on = useMounted();
  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      {[[left, leftNote, "sky"], [right, rightNote, "violet"]].map(([head, note, hue], i) => (
        <div key={i}
          className={`rounded-lg border p-2 transition-all duration-500 ${
            hue === "sky" ? "border-sky-200 bg-sky-50/60" : "border-violet-200 bg-violet-50/60"
          }`}
          style={{ opacity: on ? 1 : 0, transform: on ? "none" : "translateY(4px)",
                   transitionDelay: `${i * 90}ms` }}>
          <div className={`text-[11px] font-bold ${
            hue === "sky" ? "text-sky-800" : "text-violet-800"}`}>{head}</div>
          <div className="mt-0.5 text-[10px] leading-snug text-slate-600">{note}</div>
        </div>
      ))}
    </div>
  );
}

const SKETCHES: Record<ConceptSketchBlock["sketch"], React.ReactNode> = {
  active_vs_index: (
    <SketchPair left="Index fund" right="Active fund"
      leftNote="Tracks a benchmark at low cost. Judge tracking difference and cost."
      rightNote="Tries to beat that same benchmark after cost. Judge consistency and downside." />
  ),
  cagr_vs_xirr: (
    <SketchPair left="CAGR" right="XIRR"
      leftNote="One investment. One start date, one end date."
      rightNote="Many investments, each on its own date – what a SIP actually is." />
  ),
  sip_vs_lumpsum: (
    <SketchPair left="Lumpsum" right="SIP"
      leftNote="Buys once, at one price."
      rightNote="Buys many times – your cost becomes an average." />
  ),
  direct_vs_regular: (
    <SketchPair left="Regular" right="Direct"
      leftNote="Same portfolio, plus a distributor commission inside the expense ratio."
      rightNote="Same portfolio, no commission. The gap compounds every year." />
  ),
  etf_vs_index: (
    <SketchPair left="ETF" right="Index fund"
      leftNote="Traded on the exchange at a live price. Needs a demat account."
      rightNote="Bought from the AMC at the day's NAV." />
  ),
  expense_ratio: (
    <SketchPair left="Fund return" right="Your return"
      leftNote="The portfolio earns its gross return."
      rightNote="The expense ratio is deducted inside the NAV before published returns." />
  ),
  riskometer: (
    <SketchPair left="SEBI Riskometer" right="Owl's risk view"
      leftNote="Labels the risk of the type of assets the fund holds."
      rightNote="Compares the fund's observed falls and volatility with category peers." />
  ),
  balanced_advantage: (
    <SketchPair left="Equity" right="Debt"
      leftNote="Provides long-term growth exposure with market swings."
      rightNote="The manager adjusts this cushion as the fund's model changes its mix." />
  ),
};

function ConceptSketch({ b }: { b: ConceptSketchBlock }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{b.title}</p>
      {SKETCHES[b.sketch] ?? null}
      <p className="mt-2 text-[11.5px] leading-relaxed text-slate-600">{b.line}</p>
    </div>
  );
}

/* ────────────────────────── daily brief ────────────────────────── */
// The stored brief, inside chat. Slots are gated server-side — one that found no
// insight is absent from the payload — so every row here has something to say.
function DailyBriefBlockView({ b }: { b: DailyBriefBlock }) {
  const on = useMounted();
  const slots = (b.slots || []).filter((s) => s && s.insight);
  if (!slots.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
          Today's brief
        </p>
        {b.as_of && <span className="font-mono text-[9.5px] text-slate-400">{b.as_of}</span>}
      </div>
      <div className="mt-2 space-y-2">
        {slots.map((s, i) => (
          <div key={s.slot ?? i}
            className="border-l-2 border-sky-200 pl-2.5 transition-all duration-500"
            style={{ opacity: on ? 1 : 0, transform: on ? "none" : "translateX(-4px)",
                     transitionDelay: `${i * 70}ms` }}>
            <div className="text-[9.5px] font-semibold uppercase tracking-wide text-slate-400">
              {s.label}
              {s.is_update && (
                <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[8.5px] font-bold text-amber-800">
                  Update
                </span>
              )}
              {s.as_of && (
                <span className="ml-1.5 font-mono font-medium normal-case tracking-normal">
                  · as of {s.as_of}
                </span>
              )}
            </div>
            <p className="text-[12.5px] font-semibold leading-snug text-nyvo-900">{s.insight}</p>
            {s.metrics && s.metrics.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {s.metrics.slice(0, 3).map((m, j) => (
                  <span key={j} className="text-[10px] tabular-nums text-slate-500">
                    {m.label} <span className="font-semibold text-slate-700">{m.value}</span>
                    {typeof m.change_pct === "number" && (
                      <span className={m.change_pct > 0 ? "ml-0.5 text-emerald-700"
                                                        : "ml-0.5 text-rose-700"}>
                        {signed(m.change_pct)}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
            {s.why_it_matters && (
              <p className="mt-1 text-[10.5px] leading-snug text-slate-600">
                <span className="font-semibold text-sky-700">Why this matters:</span>{" "}
                {s.why_it_matters}
              </p>
            )}
            {s.change_since_prior && (
              <p className="mt-1 text-[10.5px] leading-snug text-amber-900">
                <span className="font-semibold">What changed:</span>{" "}
                {s.change_since_prior}
              </p>
            )}
            {s.source_links && s.source_links.length > 0 ? (
              <p className="mt-1 flex flex-wrap gap-1 font-mono text-[9px] leading-snug text-slate-400">
                <span>Source:</span>
                {s.source_links.slice(0, 2).map((source, sourceIndex) => (
                  <a
                    key={`${source.url}-${sourceIndex}`}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-sky-700 hover:underline"
                  >
                    {source.name} ↗
                  </a>
                ))}
              </p>
            ) : s.sources && s.sources.length > 0 ? (
              <p className="mt-1 font-mono text-[9px] leading-snug text-slate-400">
                Source: {s.sources.join(" · ")}
              </p>
            ) : null}
            {/* The same watch condition the brief and /feed show — the three
                surfaces must never disagree on what to watch. */}
            {s.watch_next?.condition && (
              <p className="mt-1 text-[10px] leading-snug text-slate-500">
                <span className="font-semibold text-amber-700">Watch:</span>{" "}
                {s.watch_next.condition}
                {s.watch_next.date && (
                  <span className="ml-1 font-mono text-[9.5px] text-slate-400">
                    {s.watch_next.date}
                  </span>
                )}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────── holdings overlap ────────────────────────── */
// "Do these two own the same things?" Two funds that overlap heavily give less
// diversification than the two names suggest, and nothing else on a compare says
// so. `basis` is rendered because the number is the overlap of the funds' TOP
// holdings, not of their whole books — leaving it off would overstate the claim.
/* ────────────────────────── plan compare ────────────────────────── */
// Direct vs Regular for ONE fund. The interesting cell is usually EMPTY: only
// 49 of 5,041 Regular schemes publish an expense ratio, so the Regular cost is
// rendered as an explicit "not in NYVO's book" rather than a blank that reads
// like zero. The cost gap prints only when the server computed one.
function PlanCompare({ b }: { b: PlanCompareBlock }) {
  const on = useMounted();
  const sides = [b.direct, b.regular];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        Direct vs Regular
      </p>

      <div className="mt-2 grid grid-cols-2 gap-2">
        {sides.map((s) => {
          const rated = s.plan === b.rated_plan;
          return (
            <div
              key={s.plan}
              className={`rounded-lg border p-2 ${
                rated ? "border-nyvo-200 bg-nyvo-50/60" : "border-slate-200 bg-slate-50/60"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-bold text-nyvo-900">{s.plan}</span>
                {rated && (
                  <span className="rounded-sm bg-nyvo-900 px-1 py-px text-[8.5px] font-bold uppercase tracking-wide text-white">
                    Rated
                  </span>
                )}
              </div>
              <div className="mt-1.5 text-[9.5px] uppercase tracking-wide text-slate-400">
                Expense ratio
              </div>
              {s.ter_pct != null ? (
                <div className="text-[17px] font-extrabold tabular-nums leading-tight text-nyvo-900">
                  {s.ter_pct.toFixed(2)}%
                </div>
              ) : (
                <div className="text-[11px] font-medium leading-tight text-slate-500">
                  Not in NYVO&rsquo;s book
                </div>
              )}
              {s.nav != null && (
                <div className="mt-1 text-[10px] tabular-nums text-slate-500">
                  NAV ₹{s.nav.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </div>
              )}
              <div className="mt-1 truncate text-[9.5px] text-slate-400" title={s.fund_name ?? ""}>
                {s.fund_name}
              </div>
            </div>
          );
        })}
      </div>

      {b.cost_gap_pct != null && (
        <div className="mt-2 rounded-lg bg-amber-50/70 px-2.5 py-1.5">
          <span className="text-[15px] font-extrabold tabular-nums text-amber-900">
            {b.cost_gap_pct > 0 ? "+" : ""}{b.cost_gap_pct.toFixed(2)}%
          </span>
          <span className="ml-1.5 text-[10.5px] text-slate-600">
            a year more on Regular – the distributor&rsquo;s commission
          </span>
        </div>
      )}

      {b.nav_as_of && (
        <div className={`mt-1.5 font-mono text-[9.5px] text-slate-400 transition-opacity ${on ? "opacity-100" : "opacity-0"}`}>
          NAVs as of {b.nav_as_of}
        </div>
      )}
      <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">{b.note}</p>
    </div>
  );
}

function HoldingsOverlap({ b }: { b: HoldingsOverlapBlock }) {
  const on = useMounted();
  const pct = Math.max(0, Math.min(100, b.weight_overlap_pct ?? 0));
  const tone = pct >= 40 ? "bg-amber-400" : pct >= 20 ? "bg-sky-400" : "bg-emerald-400";
  const read = pct >= 40 ? "a lot in common" : pct >= 20 ? "some in common" : "largely different";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
        What they share
      </p>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="text-[22px] font-extrabold tabular-nums leading-none text-nyvo-900">
          {pct.toFixed(1)}%
        </span>
        <span className="text-[11px] text-slate-500">of weight overlaps – {read}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={`blk-bar h-full rounded-full ${tone}`}
          style={{ width: on ? `${pct}%` : "0%" }} />
      </div>
      <div className="mt-1 text-[10px] text-slate-500">
        {b.n_shared} of {b.n_compared} top holdings appear in both.
      </div>

      {b.shared && b.shared.length > 0 && (
        <div className="mt-2.5 space-y-1">
          {/* The two names go in a legend rather than above the weight columns.
              A fund name needs far more room than a percentage does, so putting
              them in the column heads truncated both to a few characters. */}
          <div className="space-y-0.5 text-[10px] leading-snug text-slate-500">
            <div className="truncate" title={b.a_fund}>
              <span className="font-mono font-bold text-slate-400">A</span> · {b.a_fund}
            </div>
            <div className="truncate" title={b.b_fund}>
              <span className="font-mono font-bold text-slate-400">B</span> · {b.b_fund}
            </div>
          </div>
          <div className="flex items-center justify-between pt-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Held by both</span>
            <span className="flex gap-2">
              <span className="w-11 text-right">A</span>
              <span className="w-11 text-right">B</span>
            </span>
          </div>
          {b.shared.slice(0, 5).map((r) => (
            <div key={r.name} className="flex items-center justify-between gap-2">
              <span className="min-w-0 flex-1 truncate text-[11px] text-nyvo-900" title={r.name}>
                {r.name}
              </span>
              <span className="flex shrink-0 gap-2 text-[11px] tabular-nums text-slate-600">
                <span className="w-11 text-right">{r.a_pct.toFixed(1)}%</span>
                <span className="w-11 text-right">{r.b_pct.toFixed(1)}%</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="mt-2 text-[9.5px] leading-snug text-slate-400">{b.basis}</p>
    </div>
  );
}
